import GenericValidation, { dateIsValidAndMatchesFormat, isAgeOverEightTeenLessThanHundred } from '../GenericValidation';
import {
  emailValidation, nameValidation, phoneValidation, zipValidation,
} from '../../Requests/AppRequests';

export default class CommonValidation extends GenericValidation {
  static nameValidation(value, message) {
    return CommonValidation.formatValidationReturn(CommonValidation.validateName(value), message);
  }

  static selectValidation(value, message, defaultVal = '') {
    return CommonValidation
      .formatValidationReturn(CommonValidation.validateSelectOption(value, defaultVal), message);
  }

  static zipValidation(value, message = '') {
    return CommonValidation
      .formatValidationReturn(CommonValidation.validateZipCode(value), message);
  }

  static async namesValidation(firstName, lastName, showSpanish = false) {
    const fNameValid = CommonValidation.validateName(firstName);
    const lNameValid = CommonValidation.validateName(lastName);
    if (!fNameValid || !lNameValid) {
      return {
        firstNameValidation: CommonValidation
          .formatValidationReturn(fNameValid, (showSpanish && 'Nombre inválido') || 'Please enter a valid name'),
        lastNameValidation: CommonValidation
          .formatValidationReturn(lNameValid, (showSpanish && 'Nombre inválido') || 'Please enter a valid name'),
      };
    }
    const { data: { status, message } } = await nameValidation(firstName, lastName);
    const invalidFields = !status && message.indexOf(':') > 0 && message.split(':')[1].split(',').map((field) => field.trim());
    let finalMessage = message.split(':')[0];
    if (!status && showSpanish) {
      finalMessage = (message === 'Invalid Name Length' && 'Longitud de nombre inválida.') || 'Palabra inválida en el nombre.';
    }
    const firstNameValid = status || !(invalidFields.indexOf('firstName') > -1);
    const lastNameValid = status || !(invalidFields.indexOf('lastName') > -1);
    return {
      firstNameValidation: CommonValidation
        .formatValidationReturn(firstNameValid, finalMessage),
      lastNameValidation: CommonValidation
        .formatValidationReturn(lastNameValid, finalMessage),
    };
  }

  static firstNameValidation(value, showSpanish = false, translator = false, errorMessage = 'Please enter your first name') {
    return CommonValidation
      .nameValidation(value, ((!showSpanish || !translator) && errorMessage) || translator('FIRSTNAME'));
  }

  static lastNameValidation(value, showSpanish = false, translator = false, errorMessage = 'Please enter your last name') {
    return CommonValidation
      .nameValidation(value, ((!showSpanish || !translator) && errorMessage) || translator('LASTNAME'));
  }

  static dobValidation(value, showSpanish = false, translator = false, errorMessage = 'Please enter a valid date of birth') {
    const valid = dateIsValidAndMatchesFormat(value, 'MM-dd-yyyy');
    if (valid) {
      return CommonValidation.formatValidationReturn(isAgeOverEightTeenLessThanHundred(valid, value, 'MM-dd-yyyy'), ((!showSpanish || !translator) && errorMessage) || translator('FECHANAC'));
    }
    return CommonValidation.formatValidationReturn(valid, ((!showSpanish || !translator) && errorMessage) || translator('FECHANAC'));
  }

  static dobValidationTextField = (value, showSpanish) => {
    const valid = dateIsValidAndMatchesFormat(value, 'MM-dd-yyyy') || dateIsValidAndMatchesFormat(value, 'MM-d-yyyy');
    if (valid) {
      return CommonValidation.formatValidationReturn(isAgeOverEightTeenLessThanHundred(valid, value, 'MM-dd-yyyy'), showSpanish ? 'por favor ingresa una fecha de nacimiento valida' : 'Please enter a valid date of birth');
    }
    return CommonValidation.formatValidationReturn(valid, showSpanish ? 'por favor ingresa una fecha de nacimiento valida' : 'Please enter a valid date of birth');
  };

  static primaryRelationshipValidation(value, maritalStatus, showSpanish = false, translator = false, errorMessage = 'How is this driver related to the primary driver?') {
    if (maritalStatus.toLowerCase() === 'single' && value.toLowerCase() === 'spouse') {
      return CommonValidation
        .formatValidationReturn(false, 'Please review marital status and relationship to primary driver');
    }
    return CommonValidation.selectValidation(value, ((!showSpanish || !translator) && errorMessage) || translator('RELACIONCONDUCTOR'), '-1');
  }

  static additionalGenderValidation(value, showSpanish = false, translator = false, errorMessage = 'What is this drivers gender?') {
    return CommonValidation.selectValidation(value, ((!showSpanish || !translator) && errorMessage) || translator('GENERO'), '-1');
  }

  static async addressValidation(value, city, state, showSpanish = false, translator = false, errorMessage = 'Please enter a valid street address. Be sure not to add city and state.') {
    return CommonValidation
      .formatValidationReturn(await CommonValidation.validateAddress(value, city, state), ((!showSpanish || !translator) && errorMessage) || translator('DIRECCION'));
  }

  static async streetAddressValidation(props, address, state, city, zip) {
    const streetAddressResult = await GenericValidation.promptAddressValidation({
      user_id: props.uuid,
      address,
      city,
      state,
      zip,
    });
    const valid = streetAddressResult.success;
    const invalidMessage = streetAddressResult.message;
    const isBlocked = streetAddressResult.block;
    if (!valid && !isBlocked) {
      return ( // eslint-disable-next-line
        localStorage.setItem('promptAddressDialog', 'true'), CommonValidation.formatValidationReturn(false, invalidMessage || 'We need a valid home address in order to provide you with your quote.')
      );
    }
    if (!valid && isBlocked) {
      return (
        CommonValidation.formatValidationReturn(false, invalidMessage || 'We need a valid home address in order to provide you with your quote.')
      );
    }
    return CommonValidation
      .formatValidationReturn(valid, invalidMessage);
  }

  static relaxedAddressValidation(address, state, city, zip, spanish) {
    const invalidMessage = spanish ? 'Ingrese un valor válido para la dirección de la calle' : 'Please enter a valid value for street address';
    const valid = !!address && address.toString().length >= 5 && !!state && !!city && !!zip && zip.toString().length === 5;
    return CommonValidation.formatValidationReturn(valid, invalidMessage);
  }

  static cityValidation(value, showSpanish = false, translator = false, errorMessage = 'Please enter your city') {
    return CommonValidation
      .formatValidationReturn(CommonValidation.validateStringExists(value), ((!showSpanish || !translator) && errorMessage) || translator('CIUDAD'));
  }

  static stateValidation(value, showSpanish = false, translator = false, errorMessage = 'Please enter your state') {
    return CommonValidation.selectValidation(value, ((!showSpanish || !translator) && errorMessage) || translator('ESTADO'), '-1');
  }

  static async zipCodeValidation(value, props, showSpanish = false, translator = false, errorMessage = 'Please enter a valid zip code') {
    const valid = CommonValidation.validateZipCode(value);
    if (!valid) {
      return CommonValidation.formatValidationReturn(valid, ((!showSpanish || !translator) && errorMessage) || translator('CODPOSTAL'));
    }
    const { data } = await zipValidation(value);
    if (!!data && !data.message && data.length > 0) {
      const { postal_code: postalCode, city, state_short: stateShort } = data[0];
      if (city && stateShort) {
        props.handleSectionChange('contact', -1, ['city', 'state', 'zipCode'], [city, stateShort, postalCode], ['', '', '']);
        return CommonValidation.formatValidationReturn(true, '');
      }
    }
    return CommonValidation.formatValidationReturn(false, ((!showSpanish || !translator) && errorMessage) || translator('CODPOSTAL'));
  }

  static async prepopZipCodeValidationForRedux(value, props, showSpanish = false, translator = false, errorMessage = 'Please enter a valid zip code') {
    const valid = CommonValidation.validateZipCode(value);
    if (!valid) {
      return CommonValidation.formatValidationReturn(valid, ((!showSpanish || !translator) && errorMessage) || translator('CODPOSTAL'));
    }
    const { data } = await zipValidation(value);
    if (!!data && !data.message && data.length > 0) {
      const { postal_code: postalCode, city, state_short: stateShort } = data[0];
      if (city && stateShort) {
        return {
          city, stateShort, state: stateShort, zipCode: postalCode, zip: postalCode,
        };
      }
    }
    return false;
  }

  static async emailValidation(value, showSpanish = false, translator = false, errorMessage = 'Please enter a valid email address') {
    if (!CommonValidation.validateEmail(value)) {
      return CommonValidation.formatValidationReturn(false, ((!showSpanish || !translator) && errorMessage) || translator('EMAIL'));
    }
    const localStorageEmail = localStorage.getItem(btoa('email'));
    if (localStorageEmail && localStorageEmail === btoa(value)) {
      return CommonValidation.formatValidationReturn(true, ((!showSpanish || !translator) && errorMessage) || translator('EMAIL'));
    }
    const { data } = await emailValidation(value);
    const valid = data && data.status !== 'invalid';
    if (valid) {
      localStorage.setItem(btoa('email'), btoa(value || ''));
    }
    return CommonValidation.formatValidationReturn(valid, ((!showSpanish || !translator) && errorMessage) || translator('EMAIL'));
  }

  static relaxedPhoneValidation(value, uuid, showSpanish = false, translator = false, errorMessage = 'Please enter a valid phone number') {
    const valid = value.toString().length === 10 && !(Number.isNaN(value));
    return CommonValidation.formatValidationReturn(valid, ((!showSpanish || !translator) && errorMessage) || translator('TELEFONO'));
  }

  static async phoneValidation(value, uuid, showSpanish = false, translator = false, errorMessage = 'Please enter a valid phone number') {
    if (!CommonValidation.validatePhone(value)) {
      return CommonValidation.formatValidationReturn(false, ((!showSpanish || !translator) && errorMessage) || translator('TELEFONO'));
    }
    const { data } = await phoneValidation(value.replace(/-/g, ''), uuid);
    const valid = data && (data.status && data.recommendation !== 'block');
    return CommonValidation.formatValidationReturn(valid, ((!showSpanish || !translator) && errorMessage) || translator('TELEFONO'));
  }

  static decadeValidation(value, showSpanish = false, translator = false, errorMessage = 'Please enter the decade the property was built in') {
    return CommonValidation.selectValidation(value, ((!showSpanish || !translator) && errorMessage) || translator('DECADA'), '-1');
  }

  static yearBuiltValidation(value, showSpanish = false, translator = false, errorMessage = 'Please select the year the property was built in') {
    return CommonValidation.selectValidation(value, ((!showSpanish || !translator) && errorMessage) || translator('Año'), '-1');
  }

  static dwellingValidation(value, showSpanish = false, translator = false, errorMessage = 'Please select your dwelling type') {
    return CommonValidation.selectValidation(value, ((!showSpanish || !translator) && errorMessage) || translator('VIVIENDA'), '-1');
  }

  static footageValidation(value, showSpanish = false, translator = false, errorMessage = 'Please enter your home square footage', errorMessage2 = 'Square footage must be at least 500 and less than 10,000') {
    let valid = /^\d+$/.test(value);
    if (!valid) {
      return CommonValidation.formatValidationReturn(valid, ((!showSpanish || !translator) && errorMessage) || translator('PIESCUADRADOS'));
    }
    valid = parseFloat(value) >= 500 && parseFloat(value) < 10000;
    return CommonValidation.formatValidationReturn(valid, ((!showSpanish || !translator) && errorMessage2) || translator('PIESCUADRADOSRANGO'));
  }

  static yearValidation(value, showSpanish = false, translator = false, errorMessage = 'Please select a valid year') {
    return CommonValidation
      .formatValidationReturn(CommonValidation.validateYear(value), ((!showSpanish || !translator) && errorMessage) || translator('AÑO'));
  }

  static makeValidation(value, showSpanish = false, translator = false, errorMessage = 'Please select a valid make') {
    return CommonValidation.selectValidation(value, ((!showSpanish || !translator) && errorMessage) || translator('MARCA'));
  }

  static modelValidation(value, showSpanish = false, translator = false, errorMessage = 'Please select a valid model') {
    return CommonValidation.selectValidation(value, ((!showSpanish || !translator) && errorMessage) || translator('MODELO'));
  }

  static trimValidation(value, showSpanish = false, translator = false, errorMessage = 'Please select a valid trim') {
    return CommonValidation.selectValidation(value, ((!showSpanish || !translator) && errorMessage) || translator('SUBMODELO'));
  }
}
